const DEFAULT_BATCH_SIZE = 250;
const DEFAULT_BATCH_DELAY = 5;

export interface BatchLookupOptions {
  batchSize?: number;
  batchDelay?: number;
}

interface PendingLoad<TData> {
  promise: Promise<TData | null>;
  resolve: (value: TData | null) => void;
  reject: (error: unknown) => void;
}

const createPendingLoad = <TData>(): PendingLoad<TData> => {
  let resolve: PendingLoad<TData>['resolve'] = () => undefined;
  let reject: PendingLoad<TData>['reject'] = () => undefined;
  const promise = new Promise<TData | null>((res, rej) => {
    resolve = res;
    reject = rej;
  });
  return { promise, resolve, reject };
};

/**
 * Collects ids requested within `batchDelay` ms and fetches them in chunks of `batchSize`.
 * Ids with no matching record (by `getKey`) in a batch response resolve to null; a failed batch rejects every id in it.
 */
export default function createBatchLookup<TData>(
  fetchBatch: (ids: string[]) => Promise<TData[]>,
  getKey: (record: TData) => string | number,
  { batchSize = DEFAULT_BATCH_SIZE, batchDelay = DEFAULT_BATCH_DELAY }: BatchLookupOptions = {}
) {
  let pending = new Map<string, PendingLoad<TData>>();
  let scheduled = false;

  const flush = () => {
    scheduled = false;

    const batch = pending;
    pending = new Map();

    const ids = Array.from(batch.keys());

    for (let i = 0; i < ids.length; i += batchSize) {
      const batchIds = ids.slice(i, i + batchSize);

      fetchBatch(batchIds)
        .then((records) => {
          const byKey = new Map(records.map((record) => [String(getKey(record)), record]));
          batchIds.forEach((id) => batch.get(id)?.resolve(byKey.get(id) ?? null));
        })
        .catch((error) => {
          batchIds.forEach((id) => batch.get(id)?.reject(error));
        });
    }
  };

  return {
    load: (id: string | number): Promise<TData | null> => {
      const key = String(id);
      let load = pending.get(key);

      if (!load) {
        load = createPendingLoad<TData>();
        pending.set(key, load);
      }

      if (!scheduled) {
        scheduled = true;
        setTimeout(flush, batchDelay);
      }

      return load.promise;
    }
  };
}
